import { redirect } from 'react-router';

import { ApiError } from '@/api/client';
import { comoRespuesta } from '@/lib/errores';

/**
 * El catálogo pide sesión, y quien llega sin ella va al ingreso y vuelve a donde iba.
 *
 * Lo decide la API: sin cookie contesta 401 a la lista de productos, a los rubros, al PDF y al
 * Excel. Las pantallas no adivinan si hay sesión; preguntan, y si la API dice que no, mandan acá.
 * Está en un solo lugar para que el catálogo, los rubros y la cabecera armen la misma dirección.
 */

/** Una base cualquiera, sólo para que el navegador interprete una ruta como lo haría él. */
const ORIGEN_DE_PRUEBA = 'https://cking.invalid';

/**
 * La ruta a la que se vuelve después de iniciar sesión, si es de este sitio.
 *
 * `volver` viene en la dirección y la dirección la escribe cualquiera: sin este control,
 * `/login?volver=https://otro-sitio` es un enlace con la cara de CKING que termina en otra parte.
 * Sólo vale una ruta que empieza con una barra, y no con dos ni con `/\`, que el navegador lee
 * como el principio de otro dominio. Al final se le pregunta al propio intérprete de direcciones,
 * que es el que sabe de los caracteres que se borran antes de leer (`/\t/otro` es `//otro`).
 *
 * Sin `volver` devuelve `null`, para que el ingreso decida a dónde va cada rol; con uno que no
 * sirve, la portada.
 */
export function volverSeguro(volver: string | null): string | null {
  if (!volver) return null;

  const segundo = volver.charAt(1);
  const propia =
    volver.startsWith('/') &&
    segundo !== '/' &&
    segundo !== '\\' &&
    // Tabuladores, saltos de línea y demás: el navegador los saca y la ruta pasa a ser otra.
    // eslint-disable-next-line no-control-regex
    !/[\u0000-\u001F\u007F]/.test(volver) &&
    new URL(volver, ORIGEN_DE_PRUEBA).origin === ORIGEN_DE_PRUEBA;

  return propia ? volver : '/';
}

/**
 * La pantalla de ingreso, con la ruta a la que se vuelve.
 *
 * Las barras quedan sin escapar —una barra es válida en la búsqueda de una dirección— para que
 * se lea `/login?volver=/catalogo/ARB001` y no `%2Fcatalogo%2FARB001`. Lo demás sí se escapa: un
 * `?` o un `&` sueltos se mezclarían con la búsqueda del propio ingreso.
 */
export function direccionDeIngreso(volver: string): string {
  return `/login?volver=${encodeURIComponent(volver).replaceAll('%2F', '/')}`;
}

/**
 * El ingreso desde la página en la que se está, para los botones de la cabecera.
 *
 * Desde la propia pantalla de ingreso no se arma otra: quedaría `volver=/login`, y después de
 * entrar se volvería al formulario. Se conserva el `volver` que ya traía.
 */
export function ingresoDesde(ruta: string, busqueda: string): string {
  if (ruta === '/login') return `/login${busqueda}`;

  return direccionDeIngreso(`${ruta}${busqueda}`);
}

/**
 * Lo que contesta un loader del catálogo cuando la API lo rechazó.
 *
 * Sin sesión (401), al ingreso con la dirección que se estaba pidiendo. Cualquier otra cosa
 * sigue el camino de siempre: una cuenta suspendida (403) ya inició sesión y mandarla al ingreso
 * la dejaría dando vueltas, y una API caída es un corte, no una falta de permiso.
 */
export function sinSesionAlIngreso(fallo: unknown, request: Request): Response {
  if (fallo instanceof ApiError && fallo.isUnauthorized) {
    const { pathname, search } = new URL(request.url);
    return redirect(direccionDeIngreso(`${pathname}${search}`));
  }

  return comoRespuesta(fallo);
}
